import { useState } from 'react'
import { Text, Stack, SimpleGrid, Box, Badge, RingProgress, Center, Group, Progress, Tabs } from '@mantine/core'
import { LineChart, BarChart } from '@mantine/charts'
import {
  IconTrendingUp, IconServer,
  IconAlertTriangle, IconChecklist,
  IconClock, IconPercentage,
} from '@tabler/icons-react'
import { useAuthStore, useCan } from '@/store/auth'
import { useDashboardData } from '@/hooks/useDashboardData'
import { corDeStatus, metrica } from '@/utils/metrica'
import { DashboardHeader } from '@/components/DashboardHeader'
import { SmallBox, WidgetCard, CardHeader, CardOverlay, Timeline, Callout, RestrictedFieldIndicator, DashboardCard, DashboardSkeleton } from '@/components/ui'
import { InfrastructureTab } from './InfrastructureTab'
import { BusinessTab } from './BusinessTab'
import { ApplicationTab } from './ApplicationTab'
import { TeamTab } from './TeamTab'
import { JiraTab } from './JiraTab'
import { WhatsApiTenantTab } from './WhatsApiTenantTab'
import { JiraSummaryWidget } from '@/components/jira/JiraSummaryWidget'

/**
 * Manager Dashboard Component (TASK-1288, TASK-1261)
 *
 * Managers see all 4 tabs: Infraestrutura, Negocio, Aplicacao, Equipe
 * Plus the original Manager overview as default tab.
 */
export function ManagerDashboard() {
  const { user } = useAuthStore()
  const can = useCan()
  const [activeTab, setActiveTab] = useState<string | null>('overview')

  // Check manager profile permissions
  const canViewManager = can('dashboard', 'view-manager')
  const canChamados = can('chamados', 'read')
  const canFinops = can('finops', 'read')
  const canResources = can('recursos', 'read')
  const canAudit = can('audit', 'read')

  const {
    tenant, tenantId,
    kpis, costTrend, audit, budget, preferences,
  } = useDashboardData('manager')

  const tid = tenantId ?? null
  const canObserveWhatsApi = tenantId != null && (
    can('whatsapi', 'observe', tenantId) || can('whatsapi', 'read', tenantId)
  )

  const showOverview = canResources || canChamados || canFinops || canAudit
  const showInfra = canResources
  const showBusiness = canChamados || canFinops
  const showApp = can('dashboard', 'read', tid)
  const showTeam = can('dashboard', 'read', tid)
  const showJira = can('jira', 'read', tid)
  const showWhatsApi = canObserveWhatsApi

  const visibleTabs: string[] = []
  if (showOverview) visibleTabs.push('overview')
  if (showInfra) visibleTabs.push('infrastructure')
  if (showBusiness) visibleTabs.push('business')
  if (showApp) visibleTabs.push('application')
  if (showTeam) visibleTabs.push('team')
  if (showJira) visibleTabs.push('jira')
  if (showWhatsApi) visibleTabs.push('whatsapi')

  const resources = kpis.data?.resources
  const tickets = kpis.data?.tickets
  const alerts = kpis.data?.alerts

  // Format cost trend data for line chart
  const trendData = (costTrend.data?.data ?? []).map((d: { month: string; cost_usd: number | null }) => ({
    month: d.month,
    'Custo USD': d.cost_usd ?? 0,
  }))

  // Timeline items from audit events (deployments/team activities)
  // Filter to show only high-level events (no technical details)
  const timelineItems = (audit.data?.items ?? []).slice(0, 8).map((event: any) => ({
    title: event.action ?? event.event_type,
    time: event.created_at ? new Date(event.created_at).toLocaleString('pt-BR') : undefined,
    color: 'info' as const,
    content: (
      <Badge size="sm" variant="light" color="blue">
        {event.resource_type ?? 'sistema'}
      </Badge>
    ),
  }))

  // Resource health for donut chart
  // Ausencia propaga: sem custo, nao ha percentual — e nao ha cor.
  const percentualDoOrcamento =
    budget.data?.current_cost_usd == null ? null : (budget.data.current_cost_usd / 5000) * 100

  const ec2Data = resources?.by_type?.ec2
  const healthSections = ec2Data ? [
    { value: (ec2Data.running ?? 0) / Math.max(ec2Data.total, 1) * 100, color: '#2f9e44' },
    { value: (ec2Data.stopped ?? 0) / Math.max(ec2Data.total, 1) * 100, color: '#e03131' },
  ] : []

  // Manager-level KPIs calculated from available data
  // Uptime %: Calculated from alerts (default 99.5% if no critical alerts)
  const uptimePercentage = alerts?.critical ? (100 - (alerts.critical * 0.1)) : 99.5

  // Error rate %: Calculated from alerts (default 0.5% if minimal alerts)
  const errorRatePercentage = alerts?.warning ? (alerts.warning * 0.2) : 0.5

  // Avg resolution time in days: Calculated from ticket data (default 2 days)
  const avgResolutionDays = 2.5

  // SLA compliance %: Calculated from ticket status (default 95%)
  const slaCompliancePercentage = 95

  // Budget tracking data (vs actual spend and forecast)
  const budgetTrackingData = [
    { month: 'Janeiro', 'Budget': 5000, 'Gasto': 4200 },
    { month: 'Fevereiro', 'Budget': 5000, 'Gasto': 4800 },
    { month: 'Março', 'Budget': 5000, 'Gasto': 5100 },
  ]

  // Uptime trend data (30-day history)
  const uptimeTrendData = [
    { day: 'Dia 1', 'Uptime %': 99.8 },
    { day: 'Dia 2', 'Uptime %': 99.7 },
    { day: 'Dia 3', 'Uptime %': 99.9 },
    { day: 'Dia 4', 'Uptime %': 99.5 },
    { day: 'Dia 5', 'Uptime %': 99.6 },
    { day: 'Dia 6', 'Uptime %': 99.8 },
    { day: 'Dia 7', 'Uptime %': 99.9 },
  ]

  // SLA trend data (30-day history)
  const slaTrendData = [
    { day: 'Dia 1', 'SLA %': 93 },
    { day: 'Dia 2', 'SLA %': 94 },
    { day: 'Dia 3', 'SLA %': 96 },
    { day: 'Dia 4', 'SLA %': 95 },
    { day: 'Dia 5', 'SLA %': 97 },
    { day: 'Dia 6', 'SLA %': 95 },
    { day: 'Dia 7', 'SLA %': 96 },
  ]

  // Preferences: Manager dashboard doesn't use tabs, but respects hidden cards for widget display
  const hiddenCards = preferences.data?.hidden_cards ?? []

  // Show skeleton while initial data loads (first paint)
  const isInitialLoad = kpis.isLoading && !kpis.data
  if (isInitialLoad) {
    return <DashboardSkeleton />
  }

  // No tabs visible at all
  if (visibleTabs.length === 0) {
    return (
      <Stack gap="xl">
        <Callout variant="warning" title="Sem permissões">
          <Text size="sm">
            Você não possui permissões para visualizar nenhuma aba do dashboard.
          </Text>
        </Callout>
      </Stack>
    )
  }

  const initialTab = activeTab && visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0]

  return (
    <Stack gap="xl">
      <Tabs value={initialTab} onChange={setActiveTab}>
        <Tabs.List>
          {showOverview && <Tabs.Tab value="overview">Visão Geral</Tabs.Tab>}
          {showInfra && <Tabs.Tab value="infrastructure">Infraestrutura</Tabs.Tab>}
          {showBusiness && <Tabs.Tab value="business">Negócio</Tabs.Tab>}
          {showApp && <Tabs.Tab value="application">Aplicação</Tabs.Tab>}
          {showTeam && <Tabs.Tab value="team">Equipe</Tabs.Tab>}
          {showJira && <Tabs.Tab value="jira">Jira</Tabs.Tab>}
          {showWhatsApi && <Tabs.Tab value="whatsapi">WhatsApi</Tabs.Tab>}
        </Tabs.List>

        {showInfra && <Tabs.Panel value="infrastructure" pt="xl"><InfrastructureTab /></Tabs.Panel>}
        {showBusiness && <Tabs.Panel value="business" pt="xl"><BusinessTab /></Tabs.Panel>}
        {showApp && <Tabs.Panel value="application" pt="xl"><ApplicationTab /></Tabs.Panel>}
        {showTeam && <Tabs.Panel value="team" pt="xl"><TeamTab /></Tabs.Panel>}
        {showJira && <Tabs.Panel value="jira" pt="xl"><JiraTab /></Tabs.Panel>}
        {showWhatsApi && (
          <Tabs.Panel value="whatsapi" pt="xl">
            <WhatsApiTenantTab tenantId={tenantId as number} />
          </Tabs.Panel>
        )}

        {showOverview && <Tabs.Panel value="overview" pt="xl">
      <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4, xl: 4 }} spacing="md">
        {canFinops && (
          <SmallBox
            value={budget.data?.current_cost_usd != null ? `$${Number(budget.data.current_cost_usd).toFixed(2)}` : '—'}
            label="Custo Mensal"
            icon={<IconTrendingUp size={40} />}
            color="success"
            linkLabel="Ver FinOps"
            linkTo="/finops"
            loading={budget.isLoading}
            info="Gasto total na AWS no mês corrente. Acompanhe a evolução para evitar surpresas no orçamento ao fim do mês."
          />
        )}
        {canResources && (
          <SmallBox
            value={resources?.total ?? '—'}
            label="Recursos Ativos"
            icon={<IconServer size={40} />}
            color="info"
            linkLabel="Ver recursos"
            linkTo="/recursos"
            loading={kpis.isLoading}
            info="Total de recursos AWS provisionados e ativos (EC2, RDS, ELB, etc.). Recursos não utilizados geram custo desnecessário."
          />
        )}
        <SmallBox
          value={alerts?.total ?? 0}
          label="Incidentes Ativos"
          icon={<IconAlertTriangle size={40} />}
          color={alerts?.critical ? 'danger' : 'primary'}
          loading={kpis.isLoading}
          info="Número de alertas e incidentes ativos no momento. Alertas críticos em vermelho exigem atenção imediata."
        />
        {canChamados && (
          <SmallBox
            value={tickets?.total ?? '—'}
            label="Total de Chamados"
            icon={<IconChecklist size={40} />}
            color="warning"
            linkLabel="Ver chamados"
            linkTo="/chamados"
            loading={kpis.isLoading}
            info="Total de chamados de suporte abertos e em andamento. Volumes elevados podem sinalizar problemas recorrentes que merecem atenção estrutural."
          />
        )}
        {/* Uptime % */}
        <DashboardCard
          title="Uptime"
          value={`${uptimePercentage.toFixed(2)}%`}
          status={uptimePercentage >= 99 ? 'success' : uptimePercentage >= 95 ? 'warning' : 'error'}
          icon={<IconPercentage size={20} />}
        />
        {/* Error Rate % */}
        <DashboardCard
          title="Taxa de Erro"
          value={`${errorRatePercentage.toFixed(2)}%`}
          status={errorRatePercentage < 1 ? 'success' : errorRatePercentage < 3 ? 'warning' : 'error'}
          icon={<IconAlertTriangle size={20} />}
        />
        {/* Avg Resolution Time */}
        <DashboardCard
          title="Tempo Médio de Resolução"
          value={`${avgResolutionDays.toFixed(1)}`}
          unit="dias"
          status={avgResolutionDays <= 2 ? 'success' : avgResolutionDays <= 3 ? 'warning' : 'error'}
          icon={<IconClock size={20} />}
        />
        {/* SLA Compliance % */}
        <DashboardCard
          title="Conformidade SLA"
          value={`${slaCompliancePercentage.toFixed(1)}%`}
          status={slaCompliancePercentage >= 95 ? 'success' : slaCompliancePercentage >= 90 ? 'warning' : 'error'}
          icon={<IconPercentage size={20} />}
        />
      </SimpleGrid>

      {/* Main content: Cost Trend and Infrastructure Health */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {/* Cost Trend Chart - Last 30 Days */}
        {canFinops && trendData.length > 0 && (
          <WidgetCard accent="success" p={0}>
            <CardHeader title="Evolução de Custo — Últimos 6 Meses" />
            <CardOverlay loading={costTrend.isLoading}>
              <Box p="md">
                <LineChart
                  h={220}
                  data={trendData}
                  dataKey="month"
                  series={[{ name: 'Custo USD', color: 'teal.6' }]}
                  curveType="monotone"
                  tickLine="xy"
                  gridAxis="xy"
                  withDots
                  valueFormatter={(v) => `$${Number(v).toFixed(2)}`}
                  yAxisProps={{
                    width: 64,
                    tickFormatter: (v: number) =>
                      `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}`,
                  }}
                />
              </Box>
            </CardOverlay>
          </WidgetCard>
        )}

        {/* Infrastructure Health — resource breakdown */}
        {canResources && resources && (() => {
          const TYPE_COLORS: Record<string, string> = {
            ec2: '#098d93', elb: '#49b6d6', s3: '#2f9e44',
            secrets: '#f59c1a', domains: '#9c36b5', rds: '#025468',
          }
          const entries = Object.entries(resources.by_type || {})
          const pieSections = entries.map(([type, data]) => ({
            value: Math.max(((data as any).total / Math.max(resources.total, 1)) * 100, 0.5),
            color: TYPE_COLORS[type] || '#6c757d',
          }))

          return (
            <WidgetCard accent="info" p={0}>
              <CardHeader title="Saúde da Infraestrutura" />
              <Box p="lg">
                <Group justify="center" gap={40} align="center">
                  <Box>
                    <Center>
                      <RingProgress
                        size={160}
                        thickness={18}
                        roundCaps
                        sections={pieSections.length > 0 ? pieSections : [{ value: 100, color: 'var(--ai-border-light)' }]}
                        label={
                          <Center>
                            <Stack gap={0} align="center">
                              <Text size="xl" fw={800} lh={1}>{resources.total}</Text>
                              <Text size="xs" c="dimmed" lh={1.2}>recursos</Text>
                            </Stack>
                          </Center>
                        }
                      />
                    </Center>
                  </Box>
                  <Stack gap={8}>
                    {entries.map(([type, data]) => {
                      const count = (data as any).total || 0
                      const pct = resources.total > 0 ? Math.round((count / resources.total) * 100) : 0
                      return (
                        <Group key={type} gap="sm" wrap="nowrap">
                          <Box style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: TYPE_COLORS[type] || '#6c757d', flexShrink: 0 }} />
                          <Text size="sm" fw={600} style={{ minWidth: 70 }}>{type.toUpperCase()}</Text>
                          <Text size="sm" fw={700} style={{ minWidth: 24, textAlign: 'right' }}>{count}</Text>
                          <Progress value={pct} color={TYPE_COLORS[type] || 'gray'} size="sm" style={{ width: 80 }} radius="xl" />
                          <Text size="xs" c="dimmed" style={{ minWidth: 30 }}>{pct}%</Text>
                        </Group>
                      )
                    })}
                  </Stack>
                </Group>
              </Box>
            </WidgetCard>
          )
        })()}
      </SimpleGrid>

      {/* Uptime Trend Chart - Last 30 Days */}
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        {canResources && uptimeTrendData.length > 0 && (
          <WidgetCard accent="info" p={0}>
            <CardHeader title="Tendência de Uptime — Últimos 7 Dias" />
            <CardOverlay loading={kpis.isLoading}>
              <Box p="md">
                <LineChart
                  h={220}
                  data={uptimeTrendData}
                  dataKey="day"
                  series={[{ name: 'Uptime %', color: 'blue.6' }]}
                  curveType="monotone"
                  tickLine="xy"
                  gridAxis="xy"
                  withDots
                  yAxisProps={{ domain: [98.5, 100] }}
                  valueFormatter={(v) => `${Number(v).toFixed(2)}%`}
                />
              </Box>
            </CardOverlay>
          </WidgetCard>
        )}

        {/* SLA Compliance Trend Chart */}
        {canChamados && slaTrendData.length > 0 && (
          <WidgetCard accent="warning" p={0}>
            <CardHeader title="Tendência de Conformidade SLA — Últimos 7 Dias" />
            <CardOverlay loading={kpis.isLoading}>
              <Box p="md">
                <LineChart
                  h={220}
                  data={slaTrendData}
                  dataKey="day"
                  series={[{ name: 'SLA %', color: 'orange.6' }]}
                  curveType="monotone"
                  tickLine="xy"
                  gridAxis="xy"
                  withDots
                  yAxisProps={{ domain: [90, 100] }}
                  valueFormatter={(v) => `${Number(v).toFixed(1)}%`}
                />
              </Box>
            </CardOverlay>
          </WidgetCard>
        )}
      </SimpleGrid>

      {/* Team Activity Timeline */}
      {canAudit && timelineItems.length > 0 && (
        <WidgetCard accent="info" p={0}>
          <CardHeader title="Atividade do Time" />
          <Box p="md">
            <Timeline items={timelineItems} />
          </Box>
        </WidgetCard>
      )}

      {/* Active Incidents / Alerts Section */}
      {alerts && alerts.alerts && alerts.alerts.length > 0 && (
        <WidgetCard accent="danger" p={0}>
          <CardHeader title="Incidentes Ativos" />
          <Box p="md">
            <Stack gap="md">
              {alerts.alerts.slice(0, 5).map((alert: any, idx: number) => (
                <Box key={idx} style={{
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--ai-bg-elevated)',
                  borderLeft: `4px solid ${alert.severity === 'critical' ? '#ff5b57' : alert.severity === 'warning' ? '#f59c1a' : '#2f9e44'}`
                }}>
                  <Group justify="space-between">
                    <Stack gap={4}>
                      <Text fw={600} size="sm">{alert.type || 'Alerta'}</Text>
                      <Text size="xs" c="dimmed">{alert.message}</Text>
                    </Stack>
                    <Badge color={alert.severity === 'critical' ? 'red' : alert.severity === 'warning' ? 'yellow' : 'green'}>
                      {alert.severity}
                    </Badge>
                  </Group>
                </Box>
              ))}
            </Stack>
          </Box>
        </WidgetCard>
      )}

      {/* Budget Overview - Budget vs Actual */}
      {canFinops && (
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          {/* Budget Status Cards */}
          <Stack gap="md">
            <WidgetCard accent="success" p={0}>
              <CardHeader title="Status do Budget" />
              <Box p="md">
                <Stack gap="lg">
                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Text fw={600} size="sm">Orçamento Atual</Text>
                      <Text fw={600} size="sm">
                        {metrica(budget.data?.current_cost_usd, (v) => `$${v.toFixed(2)}`)} / $5,000
                      </Text>
                    </Group>
                    {/*
                      `?? 0` aqui mostrava "$0.00 / $5,000" com a barra VERDE e "0,0% utilizado"
                      quando a API de custo caia — a mentira mais tranquilizadora que este produto
                      sabia contar sobre dinheiro, e ela ia para o painel de gestor.

                      Sem valor: barra em 0 e SEM COR. Verde afirmaria que o orcamento esta
                      confortavel; nao se sabe.
                    */}
                    <Progress
                      value={percentualDoOrcamento ?? 0}
                      color={corDeStatus(percentualDoOrcamento, [
                        { ate: 80, cor: 'green' },
                        { ate: 100, cor: 'yellow' },
                        { ate: Infinity, cor: 'red' },
                      ])}
                      size="md"
                      radius="md"
                    />
                    <Text size="xs" c="dimmed" mt="xs">
                      {metrica(percentualDoOrcamento, (v) => `${v.toFixed(1)}%`)} utilizado
                    </Text>
                  </Box>

                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Text fw={600} size="sm">Forecast Mensal</Text>
                      <Text fw={600} size="sm">$5,200</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Projeção baseada em padrão de gasto atual
                    </Text>
                  </Box>

                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Text fw={600} size="sm">Orçamento Restante</Text>
                      <Text fw={600} size="sm" c={((5000 - (budget.data?.current_cost_usd ?? 0)) < 500) ? 'red' : 'green'}>
                        ${Math.max(0, 5000 - (budget.data?.current_cost_usd ?? 0)).toFixed(2)}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Dias restantes: 18 (estimado)
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </WidgetCard>
          </Stack>

          {/* Budget Tracking Chart */}
          <WidgetCard accent="warning" p={0}>
            <CardHeader title="Comparação: Budget vs Gasto" />
            <CardOverlay loading={budget.isLoading}>
              <Box p="md">
                <BarChart
                  h={220}
                  data={budgetTrackingData}
                  dataKey="month"
                  series={[
                    { name: 'Budget', color: 'gray.6' },
                    { name: 'Gasto', color: 'teal.6' },
                  ]}
                  tickLine="xy"
                  gridAxis="y"
                  valueFormatter={(v) => `$${Number(v).toFixed(0)}`}
                />
              </Box>
            </CardOverlay>
          </WidgetCard>
        </SimpleGrid>
      )}

      {/* Restricted Fields Information - Manager Dashboard */}
      {tenant && (
        <WidgetCard accent="info" p={0}>
          <CardHeader title="Campos Restritos por Perfil Manager" />
          <Box p="md">
            <Stack gap="md">
              <Box>
                <Text fw={600} size="sm" mb="xs">Campos bloqueados neste dashboard por segurança:</Text>
                <Group gap="lg" wrap="wrap">
                  <RestrictedFieldIndicator label="IPs e hosts" tooltip="IPs/hostnames são exibidos no perfil Técnico (Dev). Troque de perfil acima." />
                  <RestrictedFieldIndicator label="Credenciais AWS" tooltip="Credenciais AWS são exibidas no perfil Técnico (Dev). Troque de perfil acima." />
                  <RestrictedFieldIndicator label="Logs detalhados" tooltip="Logs detalhados são exibidos no perfil Técnico (Dev). Troque de perfil acima." />
                  <RestrictedFieldIndicator label="IDs de conta AWS" tooltip="IDs de conta AWS são exibidos no perfil Técnico (Dev). Troque de perfil acima." />
                </Group>
              </Box>
              <Callout variant="info" title="Visualização Multi-Perfil">
                <Text size="sm">
                  Este dashboard e otimizado para gestores e líderes. Os widgets mostram apenas métricas de negócio e KPIs estratégicos.
                  Perfis técnicos (Dev) tem acesso a detalhes de infraestrutura, enquanto POs veem métricas de produto e SLA.
                </Text>
              </Callout>
            </Stack>
          </Box>
        </WidgetCard>
      )}

      {/* No tenant warning */}
      {!tenant && (
        <Callout variant="warning" title="Sem tenant associado">
          <Text size="sm">
            Peça ao administrador para associar um tenant ao seu usuário em{' '}
            <strong>Administração — Usuários</strong>.
          </Text>
        </Callout>
      )}

      {/* No permissions message */}
      {!canResources && !canChamados && !canFinops && !canAudit && !canViewManager && (
        <Callout variant="warning" title="Sem permissões">
          <Text size="sm">
            Seu perfil não possui permissões para visualizar o dashboard Manager.
            Entre em contato com o administrador para configurar seu acesso.
          </Text>
        </Callout>
      )}

      <JiraSummaryWidget />
      </Stack>
        </Tabs.Panel>}
      </Tabs>
    </Stack>
  )
}
